import React from 'react';
import { Dialog, DialogContent, DialogTitle, DialogActions, Button, Typography, Box } from '@mui/material';
import Grid from '@mui/material/Grid2';

interface AboutScreenProps {
  open: boolean;
  onClose: () => void;
  _isMobile?: boolean;
  isLandscape?: boolean;
}

const AboutScreen: React.FC<AboutScreenProps> = ({ 
  open, 
  onClose,
  _isMobile = false,
  isLandscape = false 
}) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ 
        style: { 
          backgroundColor: '#121212', 
          color: 'white',
          padding: isLandscape ? '8px' : '12px',
          height: 'auto',
          maxHeight: 'none', // Remove max height to prevent scrollbars
          overflowY: 'visible', // Prevent scrollbar in landscape mode
          display: 'flex',
          flexDirection: 'column'
        } 
      }}
    >
      <DialogTitle sx={{ 
        fontSize: isLandscape ? '1.4rem' : '1.8rem', 
        textAlign: 'center', 
        py: isLandscape ? 0.5 : 1,
        mb: isLandscape ? 0 : undefined
      }}>
        About Captain's Chess
      </DialogTitle>
      
      <DialogContent sx={{ 
        overflow: 'visible', // Prevent scrollbar
        display: 'flex', 
        flexDirection: 'column',
        padding: isLandscape ? '5px' : undefined,
        paddingBottom: isLandscape ? '3px' : undefined,
        maxHeight: 'none', // Remove max height constraint
      }}>
        <Grid container spacing={isLandscape ? 0.5 : 1}>
          {isLandscape ? (
            // Landscape layout - 2 columns
            <>
              <Grid size={6}>
                <Box sx={{ mb: isLandscape ? 1 : 2 }}>
                  <Typography variant="h6" sx={{ mb: 0.5, fontSize: isLandscape ? '0.9rem' : '1.1rem' }}>Chess Pieces</Typography>
                  <Box sx={{ pl: isLandscape ? 0.5 : 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.25, fontSize: isLandscape ? '0.75rem' : undefined }}>
                      <strong>Pawn/Rook/Knight:</strong>{' '}
                      <a href="https://www.flaticon.com/free-icons/chess" title="chess icons" style={{ color: '#90caf9' }}>
                        Chess icons by Freepik - Flaticon
                      </a>
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mb: 0.25, fontSize: isLandscape ? '0.75rem' : undefined }}>
                      <strong>Bishop/Queen/King:</strong>{' '}
                      <a href="https://www.flaticon.com/free-icons/chess-piece" title="chess piece icons" style={{ color: '#90caf9' }}>
                        Chess piece icons by Freepik - Flaticon
                      </a>
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ mb: isLandscape ? 1 : 2 }}>
                  <Typography variant="h6" sx={{ mb: 0.5, fontSize: isLandscape ? '0.9rem' : '1.1rem' }}>Artwork</Typography>
                  <Box sx={{ pl: isLandscape ? 0.5 : 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.25, fontSize: isLandscape ? '0.75rem' : undefined }}>
                      <strong>Title Screens: </strong> Steve Boothby
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ mb: isLandscape ? 1 : 2 }}>
                  <Typography variant="h6" sx={{ mb: 0.5, fontSize: isLandscape ? '0.9rem' : '1.1rem' }}>Development</Typography>
                  <Box sx={{ pl: isLandscape ? 0.5 : 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.25, fontSize: isLandscape ? '0.75rem' : undefined }}>
                      <strong>Author:</strong> Steve Boothby
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid size={6}>
                <Box sx={{ mb: isLandscape ? 1 : 2 }}>
                  <Typography variant="h6" sx={{ mb: 0.5, fontSize: isLandscape ? '0.9rem' : '1.1rem' }}>Game Audio</Typography>
                  <Box sx={{ pl: isLandscape ? 0.5 : 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.25, fontSize: isLandscape ? '0.75rem' : undefined }}>
                      swoosh_v2 by migoreng1 -- <a href="https://freesound.org/s/786514/" target="_blank" rel="noreferrer" style={{ color: '#90caf9' }}>freesound.org</a> -- CC0
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.25, fontSize: isLandscape ? '0.75rem' : undefined }}>
                      Win Spacey by GameAudio -- <a href="https://freesound.org/s/220184/" target="_blank" rel="noreferrer" style={{ color: '#90caf9' }}>freesound.org</a> -- CC0
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.25, fontSize: isLandscape ? '0.75rem' : undefined }}>
                      click by 1bob -- <a href="https://freesound.org/s/717366/" target="_blank" rel="noreferrer" style={{ color: '#90caf9' }}>freesound.org</a> -- CC0
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.25, fontSize: isLandscape ? '0.75rem' : undefined }}>
                      ocean-sound.wav by quest.miller -- <a href="https://freesound.org/s/587148/" target="_blank" rel="noreferrer" style={{ color: '#90caf9' }}>freesound.org</a> -- CC0
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </>
          ) : (
            // Portrait layout - unchanged
            <>
              <Grid size={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1, fontSize: '1.1rem' }}>Chess Pieces</Typography>
                  <Box sx={{ pl: 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Pawn/Rook/Knight:</strong>{' '}
                      <a href="https://www.flaticon.com/free-icons/chess" title="chess icons" style={{ color: '#90caf9' }}>
                        Chess icons by Freepik - Flaticon
                      </a>
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Bishop/Queen/King:</strong>{' '}
                      <a href="https://www.flaticon.com/free-icons/chess-piece" title="chess piece icons" style={{ color: '#90caf9' }}>
                        Chess piece icons by Freepik - Flaticon
                      </a>
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1, fontSize: '1.1rem' }}>Game Audio</Typography>
                  <Box sx={{ pl: 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      swoosh_v2 by migoreng1 -- <a href="https://freesound.org/s/786514/" target="_blank" rel="noreferrer" style={{ color: '#90caf9' }}>freesound.org</a> -- CC0
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Win Spacey by GameAudio -- <a href="https://freesound.org/s/220184/" target="_blank" rel="noreferrer" style={{ color: '#90caf9' }}>freesound.org</a> -- CC0
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      click by 1bob -- <a href="https://freesound.org/s/717366/" target="_blank" rel="noreferrer" style={{ color: '#90caf9' }}>freesound.org</a> -- CC0
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      ocean-sound.wav by quest.miller -- <a href="https://freesound.org/s/587148/" target="_blank" rel="noreferrer" style={{ color: '#90caf9' }}>freesound.org</a> -- CC0
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid size={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1, fontSize: '1.1rem' }}>Artwork</Typography>
                  <Box sx={{ pl: 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Title Screens: </strong> Steve Boothby
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1, fontSize: '1.1rem' }}>Development</Typography>
                  <Box sx={{ pl: 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Author:</strong> Steve Boothby
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      
      <Box 
        sx={{ 
          background: 'linear-gradient(45deg, #FFD700, #8B4513, #DAA520, #CD853F, #FFD700)',
          py: isLandscape ? 0.5 : 1, 
          borderTop: '1px solid rgba(255,255,255,0.1)',
          width: '100%',
          marginTop: 'auto'
        }}
      >
        <Typography 
          variant="caption" 
          sx={{ 
            textAlign: 'center', 
            fontWeight: 'bold',
            color: 'black',
            display: 'block',
            fontSize: isLandscape ? '0.65rem' : '0.7rem'
          }}
        >
          © 2025 Steve Boothby. All rights reserved.
        </Typography>
      </Box>
      
      <DialogActions sx={{ padding: isLandscape ? '4px 8px' : undefined }}>
        <Button 
          onClick={onClose} 
          variant="contained" 
          color="primary" 
          size="small"
          sx={{ 
            fontSize: isLandscape ? '0.75rem' : undefined, 
            py: isLandscape ? 0.5 : undefined
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AboutScreen;